import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Star, Monitor, Volume2, Lightbulb, Camera, Wrench, Settings, CheckCircle, MessageCircle, ShoppingCart, Car, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getProducts, getGallery, getTestimonials } from '../services/api';
import { useCart } from '../context/CartContext';
import usePageSEO from '../hooks/usePageSEO';
import { getWhatsAppProductUrl } from '../lib/whatsapp';
import { isPriceOnRequest } from '../lib/pricing';
import {
  ALL_VALUE,
  buildBrandOptions,
  buildModelOptions,
  buildShopFilterUrl,
  buildYearOptions,
} from '../lib/productFitment';

const FEATURED_PRODUCTS_LIMIT = 4;

const shuffleProducts = (items) => {
  return [...items]
    .map((item) => ({ item, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ item }) => item);
};

const Home = () => {
  const { t, i18n } = useTranslation();
  const { addToCart } = useCart();
  usePageSEO({});
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [servicePages, setServicePages] = useState([]);
  const [vehicleCatalog, setVehicleCatalog] = useState([]);
  const [vehicleCatalogLoading, setVehicleCatalogLoading] = useState(false);
  const [selectedVehicleBrand, setSelectedVehicleBrand] = useState(ALL_VALUE);
  const [selectedVehicleModel, setSelectedVehicleModel] = useState(ALL_VALUE);
  const [selectedVehicleYear, setSelectedVehicleYear] = useState(ALL_VALUE);
  const lang = i18n.language?.substring(0, 2) || 'en';
  const selectDir = lang === 'ar' ? 'rtl' : 'ltr';

  const getIcon = (iconName) => {
    const icons = { Monitor, Volume2, Lightbulb, Camera, Wrench, Settings };
    const Icon = icons[iconName];
    return Icon ? <Icon className="h-6 w-6" /> : null;
  };

  const services = [
    { id: 1, icon: 'Monitor', titleKey: 'services.items.androidScreens.title', descKey: 'services.items.androidScreens.description' },
    { id: 2, icon: 'Volume2', titleKey: 'services.items.soundSystems.title', descKey: 'services.items.soundSystems.description' },
    { id: 3, icon: 'Lightbulb', titleKey: 'services.items.ledLighting.title', descKey: 'services.items.ledLighting.description' },
    { id: 4, icon: 'Camera', titleKey: 'services.items.backupCameras.title', descKey: 'services.items.backupCameras.description' },
    { id: 5, icon: 'Wrench', titleKey: 'services.items.installation.title', descKey: 'services.items.installation.description' },
    { id: 6, icon: 'Settings', titleKey: 'services.items.custom.title', descKey: 'services.items.custom.description' }
  ];

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadVehicleCatalog = async () => {
      try {
        setVehicleCatalogLoading(true);
        const productsData = await getProducts({ limit: 1000, sort_lang: 'en' });
        if (isMounted) setVehicleCatalog(productsData);
      } catch (error) {
        console.error('Vehicle finder products error:', error);
        if (isMounted) setVehicleCatalog([]);
      } finally {
        if (isMounted) setVehicleCatalogLoading(false);
      }
    };

    loadVehicleCatalog();
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    loadFeaturedProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const loadData = async () => {
    try {
      console.log('Loading data from API...');
      const [galleryData, testimonialsData] = await Promise.all([
        getGallery().catch(err => { console.error('Gallery error:', err); return []; }),
        getTestimonials().catch(err => { console.error('Testimonials error:', err); return []; })
      ]);
      // Load service pages
      try {
        const pagesRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pages`);
        const pagesData = await pagesRes.json();
        const pages = pagesData.filter(p => p.page_type === 'service');
        setServicePages(pages);
      } catch (e) { console.error('Pages error:', e); }
      setGallery(galleryData);
      setTestimonials(testimonialsData);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const loadFeaturedProducts = async () => {
    try {
      setProductsLoading(true);
      const productsData = await getProducts({ limit: 1000, sort_lang: lang });
      setProducts(shuffleProducts(productsData).slice(0, FEATURED_PRODUCTS_LIMIT));
    } catch (error) {
      console.error('Products error:', error);
      setProducts([]);
    } finally {
      setProductsLoading(false);
    }
  };

  const getServicePageLink = (serviceIndex) => {
    // Try to match service pages by order or index
    if (servicePages && servicePages.length > serviceIndex) {
      return `/page/${servicePages[serviceIndex].slug}`;
    }
    return '#';
  };

  const vehicleBrandOptions = useMemo(() => buildBrandOptions(vehicleCatalog, lang), [vehicleCatalog, lang]);
  const vehicleModelOptions = useMemo(() => buildModelOptions(vehicleCatalog, selectedVehicleBrand), [vehicleCatalog, selectedVehicleBrand]);
  const vehicleYearOptions = useMemo(() => buildYearOptions(vehicleCatalog, selectedVehicleBrand, selectedVehicleModel), [vehicleCatalog, selectedVehicleBrand, selectedVehicleModel]);
  const vehicleFinderUrl = useMemo(() => buildShopFilterUrl({
    brand: selectedVehicleBrand,
    model: selectedVehicleModel,
    year: selectedVehicleYear,
  }), [selectedVehicleBrand, selectedVehicleModel, selectedVehicleYear]);

  const handleVehicleBrandChange = (value) => {
    setSelectedVehicleBrand(value);
    setSelectedVehicleModel(ALL_VALUE);
    setSelectedVehicleYear(ALL_VALUE);
  };

  const handleVehicleModelChange = (value) => {
    setSelectedVehicleModel(value);
    setSelectedVehicleYear(ALL_VALUE);
  };

  const formatPrice = (product) => {
    if (isPriceOnRequest(product.price_min)) {
      return t('pages.priceOnRequest');
    }
    if (product.price_max) {
      return `${product.price_min.toFixed(3)} - ${product.price_max.toFixed(3)} ${product.currency}`;
    }
    return `${product.price_min.toFixed(3)} ${product.currency}`;
  };

  const canOrderProduct = (product) => !isPriceOnRequest(product.price_min) && product.stock_quantity > 0;

  useEffect(() => {
    const observerOptions = { threshold: 0.1, rootMargin: '0px 0px -100px 0px' };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) entry.target.classList.add('animate-in');
      });
    }, observerOptions);
    document.querySelectorAll('.fade-in-section').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative flex min-h-[900px] items-center overflow-hidden py-24 md:min-h-[860px] md:py-28 lg:min-h-[92vh]">
        <div className="absolute inset-0 z-0" style={{ backgroundImage: 'url(/hero.jpg)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/85 to-red-900/70" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <Badge className="mb-5 bg-red-600 hover:bg-red-700 text-white border-none">{t('hero.badge')}</Badge>
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-5 leading-tight">
              {t('hero.title')}
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-red-600">{t('hero.titleHighlight')}</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed">{t('hero.description')}</p>
          </div>

          <div className="mt-7 max-w-6xl rounded-lg border border-white/20 bg-white p-4 shadow-2xl md:p-5">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-center">
              <div className="flex items-start gap-3 xl:w-80">
                <div className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-red-100 text-red-600">
                  <Car className="h-10 w-10" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">{t('products.vehicleFinderTitle')}</h2>
                  <p className="text-sm text-gray-600">{t('products.vehicleFinderDesc')}</p>
                </div>
              </div>

              <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_auto]">
                <Select dir={selectDir} value={selectedVehicleBrand} onValueChange={handleVehicleBrandChange} disabled={vehicleCatalogLoading}>
                  <SelectTrigger className="h-11 [&_svg]:!h-10 [&_svg]:!w-10"><SelectValue placeholder={t('products.selectBrand')} /></SelectTrigger>
                  <SelectContent dir={selectDir}>
                    <SelectItem value={ALL_VALUE}>{t('products.allBrands')}</SelectItem>
                    {vehicleBrandOptions.map((brand) => (
                      <SelectItem key={brand.value} value={brand.value}>{brand.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  dir={selectDir}
                  value={selectedVehicleModel}
                  onValueChange={handleVehicleModelChange}
                  disabled={vehicleCatalogLoading || selectedVehicleBrand === ALL_VALUE}
                >
                  <SelectTrigger className="h-11 [&_svg]:!h-10 [&_svg]:!w-10"><SelectValue placeholder={t('products.selectModel')} /></SelectTrigger>
                  <SelectContent dir={selectDir}>
                    <SelectItem value={ALL_VALUE}>{t('products.allModels')}</SelectItem>
                    {vehicleModelOptions.map((model) => (
                      <SelectItem key={model} value={model}>{model}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  dir={selectDir}
                  value={selectedVehicleYear}
                  onValueChange={setSelectedVehicleYear}
                  disabled={vehicleCatalogLoading || selectedVehicleBrand === ALL_VALUE}
                >
                  <SelectTrigger className="h-11 [&_svg]:!h-10 [&_svg]:!w-10"><SelectValue placeholder={t('products.selectYear')} /></SelectTrigger>
                  <SelectContent dir={selectDir}>
                    <SelectItem value={ALL_VALUE}>{t('products.allYears')}</SelectItem>
                    {vehicleYearOptions.map((year) => (
                      <SelectItem key={year} value={year}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <div className="flex flex-col gap-3 sm:col-span-2 sm:flex-row lg:col-span-1">
                  <Button asChild size="lg" className="h-[40px] min-h-[40px] flex-1 bg-red-600 px-5 py-0 font-semibold leading-none hover:bg-red-700 lg:flex-none">
                    <Link to={vehicleFinderUrl}>
                      <Search className="h-4 w-4" />
                      {t('products.findProducts')}
                    </Link>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="h-[40px] min-h-[40px] flex-1 border-gray-300 px-5 py-0 font-semibold leading-none text-gray-800 hover:bg-gray-100 lg:flex-none">
                    <Link to="/contact">{t('hero.contactBtn')}</Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 grid max-w-3xl grid-cols-3 gap-4 sm:gap-6 md:mt-10">
            <div><div className="text-2xl sm:text-3xl font-bold text-white">1000+</div><div className="text-sm text-gray-400">{t('hero.stats.customers')}</div></div>
            <div><div className="text-2xl sm:text-3xl font-bold text-white">50+</div><div className="text-sm text-gray-400">{t('hero.stats.brands')}</div></div>
            <div><div className="text-2xl sm:text-3xl font-bold text-white">10+</div><div className="text-sm text-gray-400">{t('hero.stats.experience')}</div></div>
          </div>
        </div>
        <div className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 animate-bounce lg:block">
          <div className="flex h-10 w-6 justify-center rounded-full border-2 border-white"><div className="mt-2 h-3 w-1 rounded-full bg-white" /></div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white fade-in-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{t('services.title')}</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">{t('services.description')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Link key={service.id} to={getServicePageLink(index)} style={{ textDecoration: 'none' }}>
                <Card className="group hover:shadow-xl transition-all duration-300 border-gray-200 hover:border-red-600 cursor-pointer h-full" style={{ animationDelay: `${index * 100}ms` }}>
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-red-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-red-600 transition-colors">
                      <div className="text-red-600 group-hover:text-white transition-colors">{getIcon(service.icon)}</div>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{t(service.titleKey)}</h3>
                    <p className="text-gray-600">{t(service.descKey)}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="py-20 bg-gray-50 fade-in-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{t('products.title')}</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">{t('products.description')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {productsLoading ? (
              <div className="col-span-full py-12 text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600 mx-auto"></div>
              </div>
            ) : products.map((product) => (
              <Card key={product.id} className="group hover:shadow-xl transition-all duration-300 overflow-hidden">
                <Link to={`/shop/${product.id}`}>
                  <div className="relative overflow-hidden">
                    <img src={product.image_url} alt={product.name[lang]} className="h-52 w-full object-cover object-center group-hover:scale-105 transition-transform duration-500" />
                    <Badge className="absolute top-3 right-3 bg-red-600 text-white">{product.category[lang]}</Badge>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-bold text-gray-900 mb-2 line-clamp-2">{product.name[lang]}</h3>
                    <p className="text-sm text-gray-600 mb-3">{product.description[lang]}</p>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {product.specs.slice(0, 2).map((spec, idx) => (<span key={idx} className="text-xs bg-gray-100 px-2 py-1 rounded">{spec[lang]}</span>))}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-red-600">{formatPrice(product)}</span>
                      {canOrderProduct(product) ? (
                        <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={(e) => { e.preventDefault(); addToCart(product.id, 1); }} aria-label={t('pages.addToCart')}>
                          <ShoppingCart className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={(e) => { e.preventDefault(); window.open(getWhatsAppProductUrl(product.name[lang] || product.name.en || product.name.ar, product.id, lang), '_blank', 'noopener,noreferrer'); }} aria-label={t('pages.contactForPrice')}>
                          <MessageCircle className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Link>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12">
            <Button size="lg" variant="outline" className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white" asChild>
              <Link to="/shop">{t('products.viewAll')}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 bg-white fade-in-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{t('gallery.title')}</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">{t('gallery.description')}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {gallery.map((work) => (
              <div key={work.id} className="relative group overflow-hidden rounded-lg cursor-pointer aspect-video">
                <img src={work.image_url} alt={work.title[lang]} className="w-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 text-white">
                    <Badge className="mb-2 bg-red-600">{work.category[lang]}</Badge>
                    <h3 className="font-bold">{work.title[lang]}</h3>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50 fade-in-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{t('testimonials.title')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (<Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />))}
                  </div>
                  <p className="text-gray-700 mb-4 italic">"{typeof testimonial.comment === 'object' ? (testimonial.comment[lang] || testimonial.comment.en || '') : testimonial.comment}"</p>
                  <div className="flex items-center justify-between">
                    <div><div className="font-bold text-gray-900">{testimonial.customer_name}</div></div>
                    <CheckCircle className="h-6 w-6 text-green-500" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-red-600 to-red-700 text-white fade-in-section">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">{t('cta.title')}</h2>
          <p className="text-xl mb-8 text-red-100 max-w-2xl mx-auto">{t('cta.description')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" className="bg-white text-red-600 hover:bg-gray-100" asChild>
              <Link to="/contact">{t('cta.contactBtn')}</Link>
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-red-600" asChild>
              <Link to="/shop">{t('cta.browseBtn')}</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
